// Small, dependency-free charts for analytics. One measure per chart, so one colour: the brand blue.
// Thin marks, 4px rounded data ends, hairline grid, hover tooltips, and a table view for every chart.
import { esc, html, raw, type Raw } from './ui';

export const fmtNum = (n: number) => (n >= 10000 ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1)}K` : n.toLocaleString('en-AU'));
export function fmtTime(sec: number): string {
  sec = Math.round(sec);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ${String(sec % 60).padStart(2, '0')}s`;
  return `${Math.floor(sec / 3600)}h ${String(Math.floor((sec % 3600) / 60)).padStart(2, '0')}m`;
}

/** Stat tile: label, value, and the change against the previous period of the same length. */
export function statTile(label: string, value: string, now?: number, before?: number, periodLabel = 'previous period'): Raw {
  let delta: Raw | string = '';
  if (now != null && before != null) {
    if (before === 0 && now === 0) delta = '';
    else if (before === 0) delta = html`<span class="delta up"><b>▲</b> New</span><span class="vs">vs ${periodLabel}</span>`;
    else {
      const pct = Math.round(((now - before) / before) * 100);
      const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';
      delta = html`<span class="delta ${dir}"><b>${dir === 'up' ? '▲' : dir === 'down' ? '▼' : '–'}</b> ${Math.abs(pct)}%</span><span class="vs">vs ${periodLabel}</span>`;
    }
  }
  return html`<div class="box stat-tile"><span class="lbl">${label}</span><strong>${value}</strong><div class="foot">${delta}</div></div>`;
}

/** Horizontal bars for a ranked list (rooms, places, sources). Values sit at the bar tips. */
export function barList(rows: Array<{ label: string; value: number; display?: string; sub?: string; href?: string }>, empty = 'Nothing yet'): Raw {
  if (!rows.length) return html`<p class="muted small">${empty}</p>`;
  const max = Math.max(...rows.map((r) => r.value), 1);
  return html`<ol class="barlist">${rows.map((r) => {
    const label = r.href ? html`<a href="${r.href}">${r.label}</a>` : html`${r.label}`;
    return html`<li title="${r.label}: ${r.display ?? fmtNum(r.value)}">
      <div class="bl-top"><span class="bl-label">${label}${r.sub ? html`<em>${r.sub}</em>` : ''}</span><span class="bl-val">${r.display ?? fmtNum(r.value)}</span></div>
      <div class="bl-track"><i style="width:${Math.max(1.5, (r.value / max) * 100).toFixed(1)}%"></i></div>
    </li>`;
  })}</ol>`;
}

/** A plain table twin for any chart, behind a small toggle. */
export function tableView(head: string[], rows: Array<Array<string | number>>, open = true): Raw {
  return html`<details class="table-view" ${open ? raw('open') : ''}><summary>Table</summary>
    <table><thead><tr>${head.map((h) => html`<th>${h}</th>`)}</tr></thead>
    <tbody>${rows.map((r) => html`<tr>${r.map((c) => html`<td>${typeof c === 'number' ? fmtNum(c) : c}</td>`)}</tr>`)}</tbody></table></details>`;
}

function niceMax(v: number): { max: number; step: number } {
  if (v <= 4) return { max: 4, step: 1 };
  const raw = v / 4;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw)!;
  return { max: Math.ceil(v / step) * step, step };
}

export interface ColumnPoint { label: string; short: string; value: number; extra?: string }

/**
 * Columns over time (views per day or week). Draws to fit its container and redraws on resize.
 * Each column's whole band is its hover target; the tooltip shows the date, value and extra line.
 */
export function columnChart(host: HTMLElement, points: ColumnPoint[], unit: string): void {
  const tip = document.createElement('div');
  tip.className = 'chart-tip'; tip.hidden = true;
  const draw = () => {
    const W = Math.max(280, host.clientWidth);
    const H = 220; const padL = 40; const padR = 8; const padT = 12; const padB = 28;
    const plotW = W - padL - padR; const plotH = H - padT - padB;
    const { max, step } = niceMax(Math.max(...points.map((p) => p.value), 0));
    const band = plotW / Math.max(points.length, 1);
    const bw = Math.max(2, Math.min(24, band - 2));
    const y = (v: number) => padT + plotH - (v / max) * plotH;
    const ticks: number[] = []; for (let v = 0; v <= max + 1e-9; v += step) ticks.push(v);
    const every = Math.ceil(points.length / Math.max(2, Math.floor(plotW / 64)));
    let svg = `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" role="img" aria-label="${esc(unit)} per ${points.length > 92 ? 'week' : 'day'}">`;
    for (const t of ticks) svg += `<line x1="${padL}" x2="${W - padR}" y1="${y(t).toFixed(1)}" y2="${y(t).toFixed(1)}" class="grid"/><text x="${padL - 8}" y="${(y(t) + 4).toFixed(1)}" class="tick" text-anchor="end">${fmtNum(t)}</text>`;
    points.forEach((p, i) => {
      const cx = padL + band * i + band / 2; const top = y(p.value); const h = padT + plotH - top;
      if (p.value > 0) {
        const r = Math.min(4, bw / 2, h);
        const x0 = cx - bw / 2; const x1 = cx + bw / 2; const b = padT + plotH;
        svg += `<path class="col" d="M${x0.toFixed(1)} ${b}V${(top + r).toFixed(1)}Q${x0.toFixed(1)} ${top.toFixed(1)} ${(x0 + r).toFixed(1)} ${top.toFixed(1)}H${(x1 - r).toFixed(1)}Q${x1.toFixed(1)} ${top.toFixed(1)} ${x1.toFixed(1)} ${(top + r).toFixed(1)}V${b}Z"/>`;
      }
      if (i % every === 0 || i === points.length - 1) {
        if (i === points.length - 1 && i % every !== 0 && i % every < every / 2) { /* avoid crowding the last label */ } else
          svg += `<text x="${cx.toFixed(1)}" y="${H - 8}" class="tick" text-anchor="middle">${esc(p.short)}</text>`;
      }
      svg += `<rect class="hit" x="${(padL + band * i).toFixed(1)}" y="${padT}" width="${band.toFixed(1)}" height="${plotH}" data-i="${i}" tabindex="${i === points.length - 1 ? 0 : -1}"/>`;
    });
    svg += `<line x1="${padL}" x2="${W - padR}" y1="${padT + plotH}" y2="${padT + plotH}" class="axis"/></svg>`;
    host.innerHTML = svg;
    host.append(tip);
    const show = (el: SVGRectElement) => {
      const p = points[+el.dataset.i!];
      host.querySelectorAll('.hit.on').forEach((x) => x.classList.remove('on'));
      el.classList.add('on');
      tip.innerHTML = '';
      const v = document.createElement('strong'); v.textContent = `${fmtNum(p.value)} ${p.value === 1 ? unit.replace(/s$/, '') : unit}`;
      const d = document.createElement('span'); d.textContent = p.label;
      tip.append(v, d);
      if (p.extra) { const e = document.createElement('span'); e.textContent = p.extra; tip.append(e); }
      tip.hidden = false;
      const bx = +el.getAttribute('x')! + +el.getAttribute('width')! / 2;
      const left = Math.min(Math.max(bx - tip.offsetWidth / 2, 0), W - tip.offsetWidth);
      tip.style.left = `${left}px`; tip.style.top = `${Math.max(0, y(p.value) - tip.offsetHeight - 10)}px`;
    };
    host.querySelectorAll<SVGRectElement>('.hit').forEach((el) => {
      el.addEventListener('pointerenter', () => show(el));
      el.addEventListener('focus', () => show(el));
      el.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key; const i = +el.dataset.i!;
        const n = k === 'ArrowRight' ? i + 1 : k === 'ArrowLeft' ? i - 1 : -1;
        const next = host.querySelector<SVGRectElement>(`.hit[data-i="${n}"]`);
        if (next) { e.preventDefault(); el.setAttribute('tabindex', '-1'); next.setAttribute('tabindex', '0'); next.focus(); }
      });
    });
    host.onpointerleave = () => { tip.hidden = true; host.querySelectorAll('.hit.on').forEach((x) => x.classList.remove('on')); };
  };
  draw();
  let last = host.clientWidth;
  const ro = new ResizeObserver(() => { if (Math.abs(host.clientWidth - last) > 4) { last = host.clientWidth; draw(); } });
  ro.observe(host);
}

export const chartBox = (title: string, body: Raw | string, extra: Raw | string = '', sub = ''): Raw =>
  html`<section class="box chart-box"><header><h2>${title}</h2>${sub ? html`<span class="muted small">${sub}</span>` : ''}</header>${typeof body === 'string' ? raw(body) : body}${extra}</section>`;
